import { Component, Inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  APP_MODE,
  Appointment,
  APPOINTMENT_STATUS,
  BusinessObject,
  NO_SHOW_MARK,
} from '../../../models';
import { DialogService, ErrorHandlerServiceService } from '../../../services';
import {
  LibCoreLayoutModule,
  SuccessCheckAnimationDialogComponent,
  SuccessCheckAnimationDialogComponentData,
} from '../../../ui/core-layout';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { CalendarService } from '../services/calendar.service';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggle } from '@angular/material/slide-toggle';

export interface CalendarAppointmentCancellationDialogComponentData {
  mode: APP_MODE;
  businessObject: BusinessObject;
  appointment: Appointment;
  cancelAppointmentUsingLinkId?: boolean;
}

interface MapOfLocaleAndCancelText {
  [k: string]: {
    warning: string;
    blocked: string;
  };
}

@Component({
  selector: 'lib-calendar-appointment-cancellation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    TranslateModule,
    ReactiveFormsModule,
    // material design
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatRadioModule,
    MatSlideToggle,
  ],
  templateUrl: './calendar-appointment-cancellation-dialog.component.html',
  styleUrl: './calendar-appointment-cancellation-dialog.component.scss',
})
export class CalendarAppointmentCancellationDialogComponent {
  public readonly CANCELLATION_CASE = {
    NO_SHOW: 'noShow',
    OTHERS: 'others',
  };

  public readonly CANCEL_REASONS = [
    'reason-salon-asked-to-cancel',
    'reason-unforeseen-circumstance',
    'reason-better-deal',
    'reason-mistake',
    'reason-other',
  ];

  private readonly MAP_OF_LOCALE_AND_CANCEL_TEXT: MapOfLocaleAndCancelText = {
    cs: {
      warning:
        'Vaše schůzka byla zrušena, protože jste se nedostavili. Vaše online rezervace v tomto salonu může být příště zablokována!',
      blocked:
        'Vaše schůzka byla zrušena, protože jste se nedostavili. Vaše online rezervace v tomto salonu je nyní zablokována!',
    },
    de: {
      warning:
        'Ihr Termin wurde storniert, weil Sie nicht erschienen sind. Ihre Online-Buchung bei diesem Salon kann beim nächsten Mal blockiert werden!',
      blocked:
        'Ihr Termin wurde storniert, weil Sie nicht erschienen sind. Ihre Online-Buchung bei diesem Salon ist jetzt blockiert!',
    },
    en: {
      warning:
        'Your appointment has been canceled because you did not show up. Your online booking with this salon may be blocked next time!',
      blocked:
        'Your appointment has been canceled because you did not show up. Your online booking with this salon is now blocked!',
    },
    vi: {
      warning:
        'Lịch hẹn của bạn đã bị hủy vì bạn không xuất hiện. Đặt lịch trực tuyến của bạn với salon này có thể bị chặn vào lần tiếp theo!',
      blocked:
        'Lịch hẹn của bạn đã bị hủy vì bạn không xuất hiện. Đặt lịch trực tuyến của bạn với salon này hiện đã bị chặn!',
    },
    nl: {
      warning:
        'Uw afspraak is geannuleerd omdat u niet bent komen opdagen. Uw online boeking bij deze salon kan de volgende keer worden geblokkeerd!',
      blocked:
        'Uw afspraak is geannuleerd omdat u niet bent komen opdagen. Je online boeking bij deze salon is nu geblokkeerd!',
    },
  };

  step = 1;
  public mode: APP_MODE;
  public isLoading = false;

  public cancellationCaseControl: FormControl;
  public blockCustomerControl: FormControl;
  public cancelReasonControl: FormControl;

  // if numberOfNoShowAppointment >= 0 -> the appointments of this customer have been loaded
  public numberOfNoShowAppointment = -1;

  private businessObject: BusinessObject;
  private appointment: Appointment;
  private cancelAppointmentUsingLinkId: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data: CalendarAppointmentCancellationDialogComponentData,
    private dialogRef: MatDialogRef<CalendarAppointmentCancellationDialogComponent>,
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private dialogService: DialogService,
    private calendarService: CalendarService
  ) {
    this.mode = this.data?.mode;

    this.businessObject = this.data?.businessObject;
    this.appointment = this.data?.appointment;
    this.cancelAppointmentUsingLinkId = this.data?.cancelAppointmentUsingLinkId;

    this.cancellationCaseControl = new FormControl(
      this.CANCELLATION_CASE.OTHERS
    );
    this.blockCustomerControl = new FormControl(false);
    this.cancelReasonControl = new FormControl(null, [Validators.required]);
  }

  public nextStep() {
    this.step = 2;

    if (this.isNoShowCase()) {
      this.checkAllNowShowAppointmentsOfCustomer();
    }
  }

  public previousStep() {
    this.step = 1;
  }

  public isNoShowCase(): boolean {
    return (
      this.cancellationCaseControl.value === this.CANCELLATION_CASE.NO_SHOW
    );
  }

  public shouldBlockCustomer(): boolean {
    return !!this.blockCustomerControl.value;
  }

  public isAdvanceCancellation(): boolean {
    return this.mode !== APP_MODE.BOOKING;
  }

  public isAbleToCancelAppointment(): boolean {
    return (
      !this.isLoading &&
      !!this.businessObject?.id &&
      !!this.appointment?.id &&
      (this.isNoShowCase() || this.cancelReasonControl.valid)
    );
  }

  public async cancelAppointment() {
    if (!this.isAbleToCancelAppointment()) {
      return;
    }

    try {
      this.isLoading = true;

      if (this.isNoShowCase()) {
        await this.markAppointmentAsNowShow();
        await this.performCancelAppointment(this.generateNoShowReasonText());

        if (this.shouldBlockCustomer()) {
          await this.blockCustomer();
        }
      } else {
        await this.performCancelAppointment(this.cancelReasonControl.value);
      }

      this.displaySuccessCheckAnimationDialog();
      this.closeDialog(true);
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  public closeDialog(isCancelled?: boolean): void {
    this.dialogRef.close(isCancelled || false);
  }

  private markAppointmentAsNowShow() {
    const newNote =
      this.appointment?.note?.length > 0
        ? `${NO_SHOW_MARK} ${this.appointment.note}`
        : NO_SHOW_MARK;

    const appointmentToUpdate: Appointment = {
      ...this.appointment,
      note: newNote,
    };

    return this.calendarService.updateAppointmentById(appointmentToUpdate);
  }

  private performCancelAppointment(message: string) {
    if (this.cancelAppointmentUsingLinkId) {
      return this.calendarService.cancelAppointmentByLinkId(
        this.appointment?.linkId,
        message
      );
    } else {
      return this.calendarService.cancelAppointment(
        this.businessObject?.id,
        this.appointment?.id,
        message
      );
    }
  }

  private generateNoShowReasonText(): string {
    let locale = this.businessObject?.locale?.split('_')?.[0];

    if (!this.MAP_OF_LOCALE_AND_CANCEL_TEXT[locale]) {
      locale = 'en';
    }

    return this.shouldBlockCustomer()
      ? this.MAP_OF_LOCALE_AND_CANCEL_TEXT[locale].blocked
      : this.MAP_OF_LOCALE_AND_CANCEL_TEXT[locale].warning;
  }

  private async blockCustomer() {
    const customer = await this.calendarService.getCustomerById(
      this.appointment?.owner?.id
    );
    customer.blackListed = true;

    await this.calendarService.updateCustomer(customer);
  }

  private async checkAllNowShowAppointmentsOfCustomer() {
    if (this.numberOfNoShowAppointment >= 0) {
      // already loaded
      return;
    }

    try {
      this.isLoading = true;

      this.numberOfNoShowAppointment = 0;

      const appointments =
        await this.calendarService.getAllAppointmentsOfCustomer(
          this.businessObject?.id,
          this.appointment?.owner?.id
        );

      appointments?.forEach((a) => {
        if (
          a.status === APPOINTMENT_STATUS.BO_CANCELLED &&
          a.note?.startsWith(NO_SHOW_MARK)
        ) {
          this.numberOfNoShowAppointment++;
        }
      });
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  private displaySuccessCheckAnimationDialog(): void {
    this.dialogService.openDialog(
      SuccessCheckAnimationDialogComponent,
      this.dialogService.createSmallDialogConfig({
        text: 'lib_calendar.appointment-cancellation-dialog.text.appointment-cancelled-successfully',
      } as SuccessCheckAnimationDialogComponentData)
    );
  }
}
